"use client";

import { ArrowUpRight, HelpCircle } from "lucide-react";
import promo480 from "@/assets/cat-promotional-480.webp";
import promo768 from "@/assets/cat-promotional-768.webp";
import promo960 from "@/assets/cat-promotional-960.webp";
import corporate480 from "@/assets/cat-corporate-480.webp";
import corporate768 from "@/assets/cat-corporate-768.webp";
import corporate960 from "@/assets/cat-corporate-960.webp";
import awards480 from "@/assets/cat-awards-480.webp";
import awards768 from "@/assets/cat-awards-768.webp";
import awards960 from "@/assets/cat-awards-960.webp";
import event480 from "@/assets/cat-event-480.webp";
import event768 from "@/assets/cat-event-768.webp";
import event960 from "@/assets/cat-event-960.webp";
import personalized480 from "@/assets/cat-personalized-480.webp";
import personalized768 from "@/assets/cat-personalized-768.webp";
import personalized960 from "@/assets/cat-personalized-960.webp";
import equipment480 from "@/assets/cat-equipment-480.webp";
import equipment768 from "@/assets/cat-equipment-768.webp";
import equipment960 from "@/assets/cat-equipment-960.webp";
import { Reveal, MaskedHeadline } from "./motion-primitives";
import { useQuote } from "./quote-context";
import { ResponsiveImage, srcSet } from "./responsive-image";

const CARD_SIZES = "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw";

const CATS = [
  {
    title: "Branded Promotional Products",
    src: promo480,
    srcSet: srcSet([
      [promo480, 480],
      [promo768, 768],
      [promo960, 960],
    ]),
    alt: "Wooden grill scrapers laser-engraved with multiple sponsor and brand logos",
    copy: "Everyday items carrying your mark—drinkware, pens, tools and giveaways that keep working long after the handshake.",
  },
  {
    title: "Corporate and Employee Gifts",
    src: corporate480,
    srcSet: srcSet([
      [corporate480, 480],
      [corporate768, 768],
      [corporate960, 960],
    ]),
    alt: "Matte blue tumblers engraved with the Rural Municipality of Elton crest",
    copy: "Thoughtful pieces for clients, partners and teams, presented in a way that feels considered rather than generic.",
  },
  {
    title: "Awards and Recognition",
    src: awards480,
    srcSet: srcSet([
      [awards480, 480],
      [awards768, 768],
      [awards960, 960],
    ]),
    alt: "Hexagonal leather coaster laser-engraved with a custom home improvement business logo",
    copy: "Milestones, service years and standout performance—marked with something worth keeping on the shelf.",
  },
  {
    title: "Event Merchandise",
    src: event480,
    srcSet: srcSet([
      [event480, 480],
      [event768, 768],
      [event960, 960],
    ]),
    alt: "Assorted custom leather-patch baseball and trucker hats laid out on wood",
    copy: "Conferences, fundraisers, tournaments and celebrations—items guests actually want to take home.",
  },
  {
    title: "Personalized Gifts",
    src: personalized480,
    srcSet: srcSet([
      [personalized480, 480],
      [personalized768, 768],
      [personalized960, 960],
    ]),
    alt: "Navy tumbler engraved with the message My Daddy is my Hero",
    copy: "One-of-one pieces for weddings, anniversaries, retirements and the people who deserve something specific.",
  },
  {
    title: "Equipment Tags & Lamacoids",
    src: equipment480,
    srcSet: srcSet([
      [equipment480, 480],
      [equipment768, 768],
      [equipment960, 960],
    ]),
    alt: "Engraved industrial equipment tags and nameplates mounted on machinery panels",
    copy: "Durable engraved ID tags, nameplates, and lamacoids for panels, machinery, valves, and equipment—built to stay legible in the field for years.",
  },
];

export function Categories() {
  const { goToQuote } = useQuote();

  return (
    <section id="categories" className="bg-softwhite py-24 sm:py-32">
      <div className="mx-auto max-w-[1400px] px-5">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-end">
          <h2 className="font-display text-[clamp(2.2rem,5.5vw,4.5rem)] leading-[0.95] font-bold text-ink">
            <MaskedHeadline lines={["What are you", "looking to create?"]} />
          </h2>
          <Reveal delay={0.15}>
            <p className="max-w-md text-base leading-relaxed text-muted-foreground lg:pb-3">
              Start with a category, or let us help you find the right direction for
              your project.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3">
          {CATS.map((c) => (
            <article
              key={c.title}
              className="group relative flex flex-col bg-softwhite"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <ResponsiveImage
                  src={c.src}
                  srcSet={c.srcSet}
                  sizes={CARD_SIZES}
                  alt={c.alt}
                  width={768}
                  height={960}
                  className="size-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <h3 className="font-display text-2xl leading-tight font-semibold text-softwhite">
                    {c.title}
                  </h3>
                  <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-out group-hover:grid-rows-[1fr] group-focus-within:grid-rows-[1fr]">
                    <div className="overflow-hidden">
                      <p className="pt-3 text-sm leading-relaxed text-softwhite/80">
                        {c.copy}
                      </p>
                    </div>
                  </div>
                </div>
                <span className="absolute top-5 right-5 inline-flex size-10 items-center justify-center rounded-full border border-softwhite/30 text-softwhite opacity-0 transition-all duration-500 group-hover:opacity-100">
                  <ArrowUpRight className="size-4" aria-hidden />
                </span>
              </div>
              <button
                onClick={() => goToQuote({ category: c.title })}
                className="flex items-center justify-between px-6 py-5 text-left text-sm font-semibold text-ink transition-colors hover:bg-ivory"
              >
                Request a Quote for This Category
                <ArrowUpRight className="size-4 text-brass" aria-hidden />
              </button>
            </article>
          ))}

          <div className="flex flex-col justify-between bg-ink p-8 sm:col-span-2 sm:flex-row sm:items-end sm:gap-10 lg:col-span-3">
            <div>
              <HelpCircle className="size-8 text-brass" aria-hidden />
              <h3 className="mt-6 font-display text-3xl leading-tight font-semibold text-softwhite">
                Not Sure?
                <br />
                Help Me Choose
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-softwhite/70">
                Answer three quick questions and we&rsquo;ll point you to the category
                that fits your idea best.
              </p>
            </div>
            <button
              onClick={() => {
                window.dispatchEvent(new Event("mills:need-finder"));
                document.getElementById("finder")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="mt-10 inline-flex items-center justify-between rounded-full bg-brass px-6 py-3.5 text-sm font-semibold text-ink transition hover:bg-brass-soft sm:min-w-56"
            >
              Help Me Choose
              <ArrowUpRight className="size-4" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
